import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

type LanguageOption = {
  value: string;
  label: string;
  flag: string;
};

type LanguageMultiSelectProps = {
  label: string;
  placeholder: string;
  emptyLabel: string;
  options: LanguageOption[];
  values: string[];
  onToggle: (value: string) => void;
  selectedSuffix?: string;
  searchPlaceholder?: string;
  emptySearchLabel?: string;
  disabled?: boolean;
};

export function LanguageMultiSelect({
  label,
  placeholder,
  emptyLabel,
  options,
  values,
  onToggle,
  selectedSuffix,
  searchPlaceholder,
  emptySearchLabel,
  disabled = false,
}: LanguageMultiSelectProps) {
  return (
    <div className="space-y-2.5">
      <div className="truncate text-[12px] font-medium text-foreground/90">{label}</div>

      <Popover>
        <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="flex min-h-12 w-full items-center justify-between rounded-xl border border-border/50 bg-background/50 px-3.5 py-2 text-left transition-colors hover:border-border focus-ring"
        >
          <div className="min-w-0">
            <div className="truncate text-[13px] text-foreground">{placeholder}</div>
            <div className="mt-0.5 text-[10px] text-muted-foreground">
              {values.length === 0
                ? emptyLabel
                : selectedSuffix?.includes('{count}')
                  ? selectedSuffix.replace('{count}', String(values.length))
                  : `${values.length} ${selectedSuffix ?? 'selected'}`}
            </div>
          </div>

          <div className="ml-3 text-sm text-muted-foreground">+</div>
        </button>
        </PopoverTrigger>

        <PopoverContent className="w-[320px] border-border/60 bg-background p-0">
          <Command>
            <CommandInput placeholder={searchPlaceholder ?? 'Search language...'} />

            <CommandList className="max-h-[260px]">
              <CommandEmpty>{emptySearchLabel ?? 'No language found.'}</CommandEmpty>

              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => onToggle(option.value)}
                  disabled={disabled}
                  className="flex items-center justify-between px-3 py-2.5"
                >
                  <div className="flex items-center gap-3">
                    <span>{option.flag}</span>
                    <span>{option.label}</span>
                  </div>

                  {values.includes(option.value) && (
                    <div className="h-2 w-2 rounded-full bg-primary" />
                  )}
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {values.length > 0 && (
        <div className="flex min-h-[24px] flex-wrap gap-1.5">
          {values.map((value) => {
            const option = options.find((item) => item.value === value);

            if (!option) {
              return null;
            }

            return (
              <div
                key={option.value}
                className="inline-flex items-center gap-1.5 rounded-full border border-primary/10 bg-primary/[0.04] px-2 py-0.5 text-[10px] text-foreground/90"
              >
                <span>{option.flag}</span>
                <span>{option.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
